import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import BudgetLine from '../line/BudgetLine';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { SortableTree } from 'common/components/sortableTree/SortableTree';
import { useDispatch } from 'react-redux';
import { calculateAllLinesTotals, setLines, updateLinesExpressionsValues } from 'store/budget/action';
import { rewriteParentLineExpressions } from '../utils';
import { ItemChangedReason } from 'common/components/sortableTree/types';
import store from 'store/store';
import { allSelectedAtSameDepth, getTopLevelSelectedKeys, relocateLinesAfter } from 'store/budget/reducer';

interface Props {
    lines: BudgetLineDto[];
    disabled: boolean;
}

function BudgetLinesList({ lines, disabled }: Props): JSX.Element | null {
    const dispatch = useDispatch();

    const onItemsChanged = (newLines: BudgetLineDto[], reason: ItemChangedReason<BudgetLineDto>) => {
        let finalLines = newLines;

        if (reason.type === 'dropped') {
            const draggedKey = String(reason.draggedItem.key);
            const { selectedLineKeys } = store.getState().budget;
            // Operate on top-level selected keys — descendants of a selected ancestor
            // travel implicitly inside their parent's subtree, so they must not be
            // relocated as separate roots.
            const topLevelSelected = getTopLevelSelectedKeys(lines, selectedLineKeys);
            const otherSelected = topLevelSelected.filter((k: string) => k !== draggedKey && k != null);

            // Only relocate the rest when all top-level selected lines are at the
            // same depth as the dragged one. Lines at different depths can't be
            // safely flattened into a single new parent.
            if (otherSelected.length > 0 && allSelectedAtSameDepth(lines, topLevelSelected)) {
                const droppedToParentKey = reason.droppedToParent
                    ? String(reason.droppedToParent.key)
                    : null;
                finalLines = relocateLinesAfter(newLines, draggedKey, otherSelected, droppedToParentKey);
            }
        }

        const rewrittenLines = rewriteParentLineExpressions(lines, finalLines);
        dispatch(setLines(rewrittenLines));
        dispatch(calculateAllLinesTotals());
        dispatch(updateLinesExpressionsValues());
        dispatch(calculateAllLinesTotals());
    }

    return (
        <SortableTree
            items={lines}
            onItemsChanged={(newItems, reason) => onItemsChanged(newItems as BudgetLineDto[], reason)}
            TreeItemComponent={BudgetLine}
            canHaveChildren={(item) => (item.lineType === BudgetLineType.SECTION || item.lineType === BudgetLineType.ELEMENT || item.lineType === BudgetLineType.COMPONENT_ELEMENT || item.lineType === BudgetLineType.COMPONENT)}
            canRootHaveChildren={(item) => item.lineType === BudgetLineType.SECTION}
            dropAnimation={null}
            indentationWidth={20}
            disableSorting={disabled}
        />
    );
}

export default BudgetLinesList;
